import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, Checkbox, CircularProgress, Divider, IconButton, MenuItem, Paper, Select, Tooltip, Typography } from "@mui/material";
import { toast } from "react-toastify";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import Map, { Layer, Marker, Source } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import type { MapRef, LayerProps } from "react-map-gl/mapbox";
import { GET_CONFIG, UPDATE_CONFIG } from "../config/graphql";
import type { Config } from "../../__generated__/graphql";

// ─── Types (mirror of InteractiveMapEditor) ───────────────────────────────────

type CoordinateFormValue = { lat?: number; lng?: number };

type MapAreaFormValue = {
  id: string;
  name: string;
  color: string;
  coordinates: CoordinateFormValue[];
  hidden?: boolean;
  tags?: string[];
};

type MapPointFormValue = {
  id: string;
  name: string;
  category: string;
  lat?: number;
  lng?: number;
  iconName?: string | null;
  iconScale?: number | null;
  hidden?: boolean;
  tags?: string[];
};

type MapLineFormValue = {
  id: string;
  name: string;
  color: string;
  coordinates: CoordinateFormValue[];
  hidden?: boolean;
  tags?: string[];
};

type LngLat = [number, number]; // [lng, lat]

const toFiniteNumber = (value: unknown): number | undefined => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
      ? Number(value)
      : undefined;

  return typeof parsed === "number" && Number.isFinite(parsed)
    ? parsed
    : undefined;
};

// ─── Config parsing ───────────────────────────────────────────────────────────

const FALLBACK_COLOR = "#F59E0B";

function parsePrintMapConfig(configs: Config[]) {
  const storedValue = configs.find((c) => c.key === "INTERACTIVE_MAP_DATA")
    ?.value;
  if (!storedValue) return { areas: [], pointsOfInterest: [], lines: [] };
  try {
    const parsed = JSON.parse(storedValue);
    const areas: MapAreaFormValue[] = (
      Array.isArray(parsed?.areas) ? parsed.areas : []
    ).map((a: any) => ({
      id: a.id ?? "",
      name: a.name ?? "",
      color: a.color ?? FALLBACK_COLOR,
      coordinates: Array.isArray(a.coordinates) ? a.coordinates : [],
      hidden: Boolean(a.hidden),
      tags: Array.isArray(a.tags) ? a.tags.filter((t: unknown) => typeof t === "string") : [],
    }));
    const pointsOfInterest: MapPointFormValue[] = (
      Array.isArray(parsed?.pointsOfInterest) ? parsed.pointsOfInterest : []
    ).map((p: any) => ({
      id: p.id ?? "",
      name: p.name ?? "",
      category: p.category ?? "other",
      lat: toFiniteNumber(p.lat ?? p.latitude),
      lng: toFiniteNumber(p.lng ?? p.longitude),
      iconName: typeof p.iconName === "string" && p.iconName ? p.iconName.replace(/\.(png|jpg|svg)$/i, "") : null,
      iconScale: typeof p.iconScale === "number" && p.iconScale > 0 ? p.iconScale : 1,
      hidden: Boolean(p.hidden),
      tags: Array.isArray(p.tags) ? p.tags.filter((t: unknown) => typeof t === "string") : [],
    }));
    const lines: MapLineFormValue[] = (
      Array.isArray(parsed?.lines) ? parsed.lines : []
    ).map((l: any) => ({
      id: l.id ?? "",
      name: l.name ?? "",
      color: l.color ?? FALLBACK_COLOR,
      coordinates: Array.isArray(l.coordinates) ? l.coordinates : [],
      hidden: Boolean(l.hidden),
      tags: Array.isArray(l.tags) ? l.tags.filter((t: unknown) => typeof t === "string") : [],
    }));
    return { areas, pointsOfInterest, lines };
  } catch {
    return { areas: [], pointsOfInterest: [], lines: [] };
  }
}

// ─── GeoJSON helpers ──────────────────────────────────────────────────────────

const isValidLat = (value: unknown): value is number => {
  const parsed = toFiniteNumber(value);
  return parsed !== undefined && parsed >= -90 && parsed <= 90;
};

const isValidLng = (value: unknown): value is number => {
  const parsed = toFiniteNumber(value);
  return parsed !== undefined && parsed >= -180 && parsed <= 180;
};

const toLngLat = (lat: unknown, lng: unknown): LngLat | null => {
  if (!isValidLat(lat) || !isValidLng(lng)) {
    return null;
  }

  return [toFiniteNumber(lng)!, toFiniteNumber(lat)!];
};

const expandBounds = (bounds: [LngLat, LngLat]): [LngLat, LngLat] => {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const lngPadding = minLng === maxLng ? 0.0005 : 0;
  const latPadding = minLat === maxLat ? 0.0005 : 0;

  return [
    [minLng - lngPadding, minLat - latPadding],
    [maxLng + lngPadding, maxLat + latPadding],
  ];
};

function validCoords(
  coords: CoordinateFormValue[]
): LngLat[] {
  return coords
    .map((c) => toLngLat(c.lat, c.lng))
    .filter((coord): coord is LngLat => coord !== null);
}

function polygonCentroid(ring: LngLat[]): LngLat {
  const n = ring.length;
  const sum = ring.reduce(
    ([ax, ay], [x, y]) => [ax + x, ay + y],
    [0, 0]
  );
  return [sum[0] / n, sum[1] / n];
}

function buildGeoJSON(
  areas: MapAreaFormValue[],
  pois: MapPointFormValue[],
  lines: MapLineFormValue[]
) {
  const visibleAreas = areas.filter((a) => !a.hidden);
  const visiblePois = pois
    .filter((p) => !p.hidden)
    .map((p) => {
      const coordinates = toLngLat(p.lat, p.lng);
      return coordinates ? { ...p, coordinates } : null;
    })
    .filter(
      (p): p is MapPointFormValue & { coordinates: LngLat } => p !== null
    );
  const visibleLines = lines.filter((l) => !l.hidden);

  const areasFC: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: visibleAreas
      .map((area) => {
        const ring = validCoords(area.coordinates);
        if (ring.length < 3) return null;
        const closed: LngLat[] = [...ring, ring[0]];
        return {
          type: "Feature" as const,
          properties: {
            id: area.id,
            name: area.name,
            color: area.color || FALLBACK_COLOR,
          },
          geometry: {
            type: "Polygon" as const,
            coordinates: [closed],
          },
        };
      })
      .filter(Boolean) as GeoJSON.Feature[],
  };

  const areaLabelsFC: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: visibleAreas
      .map((area) => {
        const ring = validCoords(area.coordinates);
        if (ring.length < 3 || !area.name) return null;
        const center = polygonCentroid(ring);
        return {
          type: "Feature" as const,
          properties: { name: area.name },
          geometry: { type: "Point" as const, coordinates: center },
        };
      })
      .filter(Boolean) as GeoJSON.Feature[],
  };

  const poisFC: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: visiblePois.map((p) => ({
      type: "Feature" as const,
      properties: { id: p.id, name: p.name, category: p.category, iconName: p.iconName ?? null, iconScale: p.iconScale ?? 1 },
      geometry: {
        type: "Point" as const,
        coordinates: p.coordinates,
      },
    })),
  };

  const linesFC: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: visibleLines
      .map((line) => {
        const coords = validCoords(line.coordinates);
        if (coords.length < 2) return null;
        return {
          type: "Feature" as const,
          properties: {
            id: line.id,
            name: line.name,
            color: line.color || FALLBACK_COLOR,
          },
          geometry: { type: "LineString" as const, coordinates: coords },
        };
      })
      .filter(Boolean) as GeoJSON.Feature[],
  };

  // Compute bounding box over all features
  const allLngs: number[] = [];
  const allLats: number[] = [];
  visibleAreas.forEach((a) =>
    validCoords(a.coordinates).forEach(([lng, lat]) => {
      allLngs.push(lng);
      allLats.push(lat);
    })
  );
  visiblePois.forEach((p) => {
    allLngs.push(p.coordinates[0]);
    allLats.push(p.coordinates[1]);
  });
  visibleLines.forEach((l) =>
    validCoords(l.coordinates).forEach(([lng, lat]) => {
      allLngs.push(lng);
      allLats.push(lat);
    })
  );

  const minLng = Math.min(...allLngs);
  const minLat = Math.min(...allLats);
  const maxLng = Math.max(...allLngs);
  const maxLat = Math.max(...allLats);
  const bounds =
    allLngs.length > 0 &&
    isFinite(minLng) && isFinite(minLat) &&
    isFinite(maxLng) && isFinite(maxLat)
      ? ([
          [minLng, minLat],
          [maxLng, maxLat],
        ] as [LngLat, LngLat])
      : null;

  return { areasFC, areaLabelsFC, poisFC, linesFC, bounds };
}


// ─── Layer definitions ────────────────────────────────────────────────────────

const areaFillLayer: LayerProps = {
  id: "area-fill",
  type: "fill",
  paint: {
    "fill-color": ["get", "color"],
    "fill-opacity": [
      "interpolate",
      ["linear"],
      ["zoom"],
      15,
      0.6,
      16,
      0.75,
      18,
      0.9,
    ],
  },
};

const areaOutlineLayer: LayerProps = {
  id: "area-outline",
  type: "line",
  layout: { "line-join": "round" },
  paint: {
    "line-color": ["get", "color"],
    "line-opacity": 0.9,
    "line-width": 2.5,
    "line-blur": 0.4,
  },
};

const lineLayer: LayerProps = {
  id: "line-layer",
  type: "line",
  layout: { "line-join": "round", "line-cap": "round" },
  paint: {
    "line-color": ["get", "color"],
    "line-opacity": 0.9,
    "line-width": 2.5,
  },
};

const poiCircleLayer: LayerProps = {
  id: "poi-circle",
  type: "circle",
  filter: ["!", ["==", ["slice", ["coalesce", ["get", "iconName"], ""], 0, 7], "custom_"]],
  paint: {
    "circle-color": "rgba(255, 248, 236, 0.96)",
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["zoom"],
      15,
      11,
      20,
      26,
    ],
    "circle-stroke-color": "rgba(255,255,255,0.95)",
    "circle-stroke-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      15,
      2.4,
      20,
      4.2,
    ],
  },
};

const CUSTOM_ICON_NAMES = [
  "custom_access",
  "custom_axiom_stage",
  "custom_bar_icon",
  "custom_cave_stage",
  "custom_enterance",
  "custom_family",
  "custom_food",
  "custom_forest",
  "custom_forest_stage",
  "custom_headphones",
  "custom_info",
  "custom_main_stage",
  "custom_market",
  "custom_marshall_logo",
  "custom_medical",
  "custom_merch",
  "custom_play",
  "custom_shower",
  "custom_toilets",
  "custom_word_stage",
] as const;

function normalizeImageTo64(image: HTMLImageElement | ImageBitmap): ImageData {
  const ICON_SIZE = 64;
  const canvas = document.createElement("canvas");
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext("2d")!;
  const srcW = (image as HTMLImageElement).width ?? (image as ImageBitmap).width;
  const srcH = (image as HTMLImageElement).height ?? (image as ImageBitmap).height;
  const scale = Math.min(ICON_SIZE / srcW, ICON_SIZE / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  ctx.drawImage(image as CanvasImageSource, (ICON_SIZE - drawW) / 2, (ICON_SIZE - drawH) / 2, drawW, drawH);
  return ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE);
}

function loadMapIcons(map: mapboxgl.Map): Promise<void> {
  return Promise.all(
    CUSTOM_ICON_NAMES.map(
      (name) =>
        new Promise<void>((resolve) => {
          if (map.hasImage(name)) { resolve(); return; }
          map.loadImage(`${window.location.origin}/map-icons/${name}.png`, (error, image) => {
            if (!error && image && !map.hasImage(name)) {
              map.addImage(name, normalizeImageTo64(image as HTMLImageElement | ImageBitmap));
            }
            resolve();
          });
        })
    )
  ).then(() => undefined);
}

// Filter expressions — split POIs into text-icon vs custom-image
const IS_CUSTOM_ICON: mapboxgl.Expression = [
  "==",
  ["slice", ["coalesce", ["get", "iconName"], ""], 0, 7],
  "custom_",
];
const IS_NOT_CUSTOM_ICON: mapboxgl.Expression = ["!", IS_CUSTOM_ICON];

const poiIconLayer: LayerProps = {
  id: "poi-icon",
  type: "symbol",
  filter: IS_NOT_CUSTOM_ICON,
  layout: {
    "text-field": [
      "match",
      ["get", "category"],
      "stage", "♪",
      "food", "F",
      "vendor", "V",
      "bar", "B",
      "merch", "M",
      "info", "i",
      "accessibility", "A",
      "•",
    ],
    "text-anchor": "center",
    "text-size": ["interpolate", ["linear"], ["zoom"], 15, 8, 20, 14],
    "text-allow-overlap": true,
    "text-ignore-placement": true,
  },
  paint: {
    "text-color": "#5a4a3a",
  },
};

const poiCustomIconLayer: LayerProps = {
  id: "poi-custom-icon",
  type: "symbol",
  filter: IS_CUSTOM_ICON,
  layout: {
    "icon-image": ["get", "iconName"],
    "icon-size": [
      "interpolate", ["linear"], ["zoom"],
      15, ["*", ["coalesce", ["get", "iconScale"], 1], 0.5625],
      20, ["*", ["coalesce", ["get", "iconScale"], 1], 1.125],
    ],
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
    "icon-anchor": "center",
  },
};

const poiLabelLayer: LayerProps = {
  id: "poi-labels",
  type: "symbol",
  layout: {
    "text-field": ["get", "name"],
    "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
    "text-anchor": "top",
    "text-offset": [0, 1.4],
    "text-size": ["interpolate", ["linear"], ["zoom"], 17, 11, 20, 14],
    "text-max-width": 10,
  },
  paint: {
    "text-color": "#fff9f0",
    "text-halo-color": "rgba(0,0,0,0.55)",
    "text-halo-width": 1.2,
  },
};

const areaLabelLayer: LayerProps = {
  id: "area-labels",
  type: "symbol",
  layout: {
    "text-field": ["get", "name"],
    "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
    "text-size": ["interpolate", ["linear"], ["zoom"], 15, 12, 18, 16],
    "text-allow-overlap": false,
  },
  paint: {
    "text-color": "#fff",
    "text-halo-color": "rgba(0,0,0,0.45)",
    "text-halo-width": 1.5,
    "text-halo-blur": 0.5,
  },
};

// ─── Export quality ───────────────────────────────────────────────────────────

const MAP_TOKEN = "pk.eyJ1IjoiZHR5cnJlbGwiLCJhIjoiY2x6Y3FnYXhyMGQwMDJqczNsbGhyOHY2ayJ9.O1kIOYcYxO-LLqvEcriQjQ";

type ExportQuality = "standard" | "high" | "ultra";

const EXPORT_PIXEL_RATIO: Record<ExportQuality, number> = {
  standard: 2,
  high: 3,
  ultra: 4,
};

// ─── Component ────────────────────────────────────────────────────────────────

export const PrintMap = () => {
  const { data, loading } = useQuery(GET_CONFIG);
  const mapRef = useRef<MapRef>(null);

  const configs = useMemo(
    () => (data?.configs as Config[]) ?? [],
    [data]
  );

  const { areas, pointsOfInterest, lines } = useMemo(
    () => parsePrintMapConfig(configs),
    [configs]
  );

  const [tagVisibility, setTagVisibility] = useState<Record<string, boolean>>({});

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    areas.forEach((a) => a.tags?.forEach((t) => tagSet.add(t)));
    pointsOfInterest.forEach((p) => p.tags?.forEach((t) => tagSet.add(t)));
    lines.forEach((l) => l.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [areas, pointsOfInterest, lines]);

  const isTagVisible = useCallback((tag: string) => tagVisibility[tag] ?? true, [tagVisibility]);

  const toggleTag = useCallback((tag: string) => {
    setTagVisibility((prev) => ({ ...prev, [tag]: !(prev[tag] ?? true) }));
  }, []);

  const [featureVisibility, setFeatureVisibility] = useState<Record<string, boolean>>({});
  const isFeatureVisible = useCallback((key: string) => featureVisibility[key] ?? true, [featureVisibility]);
  const toggleFeature = useCallback((key: string) => {
    setFeatureVisibility((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  }, []);

  const filteredAreas = useMemo(
    () => areas
      .filter((a) => !a.tags?.some((t) => !isTagVisible(t)))
      .filter((a) => isFeatureVisible(`area_${a.id}`)),
    [areas, tagVisibility, featureVisibility] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredPoints = useMemo(
    () => pointsOfInterest.filter((p) => !p.tags?.some((t) => !isTagVisible(t))),
    [pointsOfInterest, tagVisibility] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredLines = useMemo(
    () => lines
      .filter((l) => !l.tags?.some((t) => !isTagVisible(t)))
      .filter((l) => isFeatureVisible(`line_${l.id}`)),
    [lines, tagVisibility, featureVisibility] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { areasFC, areaLabelsFC, poisFC, linesFC, bounds } = useMemo(
    () => buildGeoJSON(filteredAreas, filteredPoints, filteredLines),
    [filteredAreas, filteredPoints, filteredLines]
  );

  const customIconPois = useMemo(
    () =>
      filteredPoints.filter(
        (p) =>
          typeof p.iconName === "string" &&
          p.iconName.startsWith("custom_") &&
          isValidLat(p.lat) &&
          isValidLng(p.lng)
      ) as (MapPointFormValue & { lat: number; lng: number; iconName: string })[],
    [filteredPoints]
  );

  // ─── Visibility / scale controls ─────────────────────────────────────────────

  type GroupState = { icon: boolean; label: boolean; scale: number };

  const categories = useMemo(
    () =>
      Array.from(new Set(pointsOfInterest.filter((p) => !p.iconName?.startsWith("custom_")).map((p) => p.category))).sort(),
    [pointsOfInterest]
  );

  const uniqueCustomIcons = useMemo(
    () => Array.from(new Set(customIconPois.map((p) => p.iconName))).sort(),
    [customIconPois]
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [visibility, setVisibility] = useState<Record<string, GroupState>>({});
  const [saving, setSaving] = useState(false);
  const printSettingsLoaded = useRef(false);

  // Load persisted print settings once configs arrive
  useEffect(() => {
    if (printSettingsLoaded.current) return;
    const stored = configs.find((c) => c.key === "PRINT_MAP_DATA")?.value;
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === "object") {
        if (parsed.groups) {
          setVisibility(parsed.groups);
          if (parsed.tags && typeof parsed.tags === "object") setTagVisibility(parsed.tags);
          if (parsed.features && typeof parsed.features === "object") setFeatureVisibility(parsed.features);
        } else {
          // Legacy format (plain groups object)
          setVisibility(parsed);
        }
        printSettingsLoaded.current = true;
      }
    } catch {}
  }, [configs]);

  const [updateConfig] = useMutation(UPDATE_CONFIG);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateConfig({
        variables: {
          settings: [{
            key: "PRINT_MAP_DATA",
            value: JSON.stringify({ groups: visibility, tags: tagVisibility, features: featureVisibility }),
          }],
        },
      });
      toast("Map settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  }, [updateConfig, visibility, tagVisibility, featureVisibility]);

  const getScale = useCallback((key: string) => visibility[key]?.scale ?? 1, [visibility]);

  const isIconVisible = useCallback((key: string) => visibility[key]?.icon ?? true, [visibility]);
  const isLabelVisible = useCallback((key: string) => visibility[key]?.label ?? true, [visibility]);

  const toggleVis = useCallback((key: string, field: "icon" | "label") => {
    setVisibility((prev) => ({
      ...prev,
      [key]: {
        icon: prev[key]?.icon ?? true,
        label: prev[key]?.label ?? true,
        scale: prev[key]?.scale ?? 1,
        [field]: !(prev[key]?.[field] ?? true),
      },
    }));
  }, []);

  const setScale = useCallback((key: string, value: number) => {
    setVisibility((prev) => ({
      ...prev,
      [key]: {
        icon: prev[key]?.icon ?? true,
        label: prev[key]?.label ?? true,
        scale: Math.max(0.25, Math.min(10, isNaN(value) ? 1 : value)),
      },
    }));
  }, []);

  // match expression: maps each category to its scale multiplier
  const categoryScaleExpr = useMemo<any>(() => {
    if (categories.length === 0) return 1;
    const pairs = categories.flatMap((c) => [c, getScale(c)]);
    return ["match", ["get", "category"], ...pairs, 1];
  }, [categories, visibility]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dynamic paint/layout driven by per-category scale
  const dynamicCirclePaint = useMemo(
    () => ({
      "circle-color": "rgba(255, 248, 236, 0.96)",
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 15, ["*", categoryScaleExpr, 11], 20, ["*", categoryScaleExpr, 26]],
      "circle-stroke-color": "rgba(255,255,255,0.95)",
      "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 15, ["*", categoryScaleExpr, 2.4], 20, ["*", categoryScaleExpr, 4.2]],
    }),
    [categoryScaleExpr]
  );

  const dynamicTextSize = useMemo(
    () => ["interpolate", ["linear"], ["zoom"], 15, ["*", categoryScaleExpr, 8], 20, ["*", categoryScaleExpr, 14]],
    [categoryScaleExpr]
  );

  // Dynamic filter for circle + text-glyph layers (category POIs only)
  const categoryIconFilter = useMemo(() => {
    const hidden = categories.filter((c) => !isIconVisible(c));
    const notCustom: any = ["!", ["==", ["slice", ["coalesce", ["get", "iconName"], ""], 0, 7], "custom_"]];
    if (hidden.length === 0) return notCustom;
    return ["all", notCustom, ["!", ["in", ["get", "category"], ["literal", hidden]]]];
  }, [categories, visibility]); // eslint-disable-line react-hooks/exhaustive-deps

  // Label layer only handles category POIs — custom icon POIs get HTML labels so
  // we can z-order them above the icon markers.
  const labelFilter = useMemo(() => {
    const notCustom: any = ["!", ["==", ["slice", ["coalesce", ["get", "iconName"], ""], 0, 7], "custom_"]];
    const hiddenCats = categories.filter((c) => !isLabelVisible(c));
    if (hiddenCats.length === 0) return notCustom;
    return ["all", notCustom, ["!", ["in", ["get", "category"], ["literal", hiddenCats]]]];
  }, [categories, visibility]); // eslint-disable-line react-hooks/exhaustive-deps

  // Custom icon HTML markers filtered by visibility
  const visibleCustomIconPois = useMemo(
    () => customIconPois.filter((p) => isIconVisible(p.iconName)),
    [customIconPois, visibility] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Area labels as HTML Markers so they appear above custom icon images
  const visibleAreaLabels = useMemo(
    () =>
      filteredAreas
        .filter((a) => a.name && isFeatureVisible(`area_label_${a.id}`))
        .map((a) => {
          const ring = validCoords(a.coordinates);
          if (ring.length < 3) return null;
          const [lng, lat] = polygonCentroid(ring);
          return { id: a.id, name: a.name, lng, lat };
        })
        .filter(Boolean) as { id: string; name: string; lng: number; lat: number }[],
    [filteredAreas, featureVisibility] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Category POI labels rendered as HTML Markers so they sit above icon markers in z-order
  const visibleCategoryPoisWithLabels = useMemo(
    () =>
      filteredPoints.filter(
        (p) =>
          !p.hidden &&
          (!p.iconName || !p.iconName.startsWith("custom_")) &&
          isValidLat(p.lat) &&
          isValidLng(p.lng) &&
          isLabelVisible(p.category)
      ) as (MapPointFormValue & { lat: number; lng: number })[],
    [filteredPoints, visibility] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [pageSize, setPageSize] = useState("A4 landscape");
  const [exportQuality, setExportQuality] = useState<ExportQuality>("high");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    const currentMap = mapRef.current?.getMap();
    if (!currentMap) return;
    setIsExporting(true);

    const pixelRatio = EXPORT_PIXEL_RATIO[exportQuality];
    const w = window.innerWidth;
    const h = window.innerHeight;

    const container = document.createElement("div");
    container.style.cssText = `position:fixed;left:${-(w + 200)}px;top:0;width:${w}px;height:${h}px;`;
    document.body.appendChild(container);

    let exportMap: mapboxgl.Map | null = null;
    try {
      exportMap = new mapboxgl.Map({
        container,
        style: "mapbox://styles/mapbox/light-v10",
        center: currentMap.getCenter(),
        zoom: currentMap.getZoom(),
        bearing: currentMap.getBearing(),
        pitch: currentMap.getPitch(),
        preserveDrawingBuffer: true,
        interactive: false,
        accessToken: MAP_TOKEN,
        pixelRatio,
      } as any);

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(
          () => reject(new Error("Export timed out")),
          30_000
        );

        exportMap!.once("load", async () => {
          // Load all icons before adding layers so they're available on first render
          await loadMapIcons(exportMap!);

          // Bake per-type scale multipliers into GeoJSON for export rendering
          const exportPoisFC: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: poisFC.features.map((f) => {
              const iconName = f.properties?.iconName;
              if (typeof iconName === "string" && iconName.startsWith("custom_")) {
                const effectiveScale = (f.properties?.iconScale ?? 1) * getScale(iconName);
                return { ...f, properties: { ...f.properties, iconScale: effectiveScale } };
              }
              return f;
            }),
          };

          exportMap!.addSource("areas", { type: "geojson", data: areasFC });
          exportMap!.addSource("lines", { type: "geojson", data: linesFC });
          exportMap!.addSource("pois", { type: "geojson", data: exportPoisFC });
          exportMap!.addSource("area-labels", { type: "geojson", data: areaLabelsFC });

          exportMap!.addLayer({ ...(areaFillLayer as any), source: "areas" });
          exportMap!.addLayer({ ...(areaOutlineLayer as any), source: "areas" });
          exportMap!.addLayer({ ...(lineLayer as any), source: "lines" });
          exportMap!.addLayer({ ...(poiCircleLayer as any), source: "pois", filter: categoryIconFilter, paint: dynamicCirclePaint });
          exportMap!.addLayer({ ...(poiIconLayer as any), source: "pois", filter: categoryIconFilter, layout: { ...poiIconLayer.layout, "text-size": dynamicTextSize } });
          exportMap!.addLayer({ ...(poiCustomIconLayer as any), source: "pois" });
          exportMap!.addLayer({ ...(poiLabelLayer as any), source: "pois", filter: labelFilter });
          exportMap!.addLayer({ ...(areaLabelLayer as any), source: "area-labels" });

          exportMap!.once("idle", () => {
            clearTimeout(timeout);
            resolve();
          });
        });

        exportMap!.on("error", (e) => {
          clearTimeout(timeout);
          reject(e.error);
        });
      });

      const canvas = exportMap.getCanvas();
      const dataUrl = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.download = `festival-map-${canvas.width}x${canvas.height}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Map export failed:", err);
    } finally {
      exportMap?.remove();
      document.body.removeChild(container);
      setIsExporting(false);
    }
  }, [exportQuality, areasFC, areaLabelsFC, poisFC, linesFC, getScale, categoryIconFilter, dynamicCirclePaint, dynamicTextSize, labelFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "print-map-css";
    style.textContent = `
      @media print {
        .no-print { display: none !important; }
        body, html { margin: 0; padding: 0; }
        @page { size: ${pageSize}; margin: 0; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [pageSize]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100vw",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // if (!process.env.REACT_APP_MAPBOX_TOKEN) {
  //   return (
  //     <Box sx={{ p: 4 }}>
  //       <Typography color="error">
  //         REACT_APP_MAPBOX_TOKEN is not set. Add it to your .env file.
  //       </Typography>
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ position: "fixed", inset: 0 }}>
      {/* Cog toggle — always visible, hidden when printing */}
      <Box className="no-print" sx={{ position: "absolute", top: 16, left: 16, zIndex: 11, display: "flex", gap: 1, alignItems: "center" }}>
        <Tooltip title={settingsOpen ? "Hide settings" : "Show settings"}>
          <IconButton
            size="small"
            onClick={() => setSettingsOpen((v) => !v)}
            sx={{ bgcolor: "background.paper", boxShadow: 1, "&:hover": { bgcolor: "background.paper" } }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {settingsOpen && (
          <>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              sx={{ bgcolor: "background.paper", minWidth: 160 }}
              disabled={isExporting}
            >
              <MenuItem value="A4 landscape">A4 Landscape</MenuItem>
              <MenuItem value="A4 portrait">A4 Portrait</MenuItem>
              <MenuItem value="A3 landscape">A3 Landscape</MenuItem>
              <MenuItem value="A3 portrait">A3 Portrait</MenuItem>
            </Select>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={isExporting}>
              Print
            </Button>
            <Select
              size="small"
              value={exportQuality}
              onChange={(e) => setExportQuality(e.target.value as ExportQuality)}
              sx={{ bgcolor: "background.paper", minWidth: 160 }}
              disabled={isExporting}
            >
              <MenuItem value="standard">Standard (2×)</MenuItem>
              <MenuItem value="high">High res (3×)</MenuItem>
              <MenuItem value="ultra">Ultra (4×) — slow</MenuItem>
            </Select>
            <Button
              variant="outlined"
              startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={isExporting}
              sx={{ bgcolor: "background.paper" }}
            >
              {isExporting ? "Exporting…" : "Export image"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => window.close()}
              sx={{ bgcolor: "background.paper" }}
              disabled={isExporting}
            >
              Close
            </Button>
          </>
        )}
      </Box>

      {/* Visibility / scale panel — only when settings open, hidden when printing */}
      {settingsOpen && (categories.length > 0 || uniqueCustomIcons.length > 0 || areas.length > 0 || lines.length > 0 || allTags.length > 0) && (
        <Paper
          className="no-print"
          elevation={3}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            p: 1.5,
            minWidth: 240,
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
          }}
        >
          {/* Column headers */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="overline" sx={{ flex: 1, lineHeight: 1 }}>
              Layers
            </Typography>
            <Tooltip title="Scale"><Typography variant="caption" sx={{ width: 52, textAlign: "center" }}>Scale</Typography></Tooltip>
            <Tooltip title="Icon"><Typography variant="caption" sx={{ width: 28, textAlign: "center" }}>I</Typography></Tooltip>
            <Tooltip title="Label"><Typography variant="caption" sx={{ width: 28, textAlign: "center" }}>L</Typography></Tooltip>
          </Box>

          {categories.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Categories</Typography>
              {categories.map((cat) => (
                <Box key={cat} sx={{ display: "flex", alignItems: "center", py: 0.25 }}>
                  <Typography variant="body2" sx={{ flex: 1, textTransform: "capitalize" }} noWrap>{cat}</Typography>
                  <input
                    type="number"
                    min={0.25}
                    max={10}
                    step={0.25}
                    value={getScale(cat)}
                    onChange={(e) => setScale(cat, parseFloat(e.target.value))}
                    style={{ width: 48, fontSize: 12, padding: "2px 4px", border: "1px solid #ccc", borderRadius: 4, marginRight: 2 }}
                  />
                  <Checkbox size="small" checked={isIconVisible(cat)} onChange={() => toggleVis(cat, "icon")} sx={{ p: 0.25 }} />
                  <Checkbox size="small" checked={isLabelVisible(cat)} onChange={() => toggleVis(cat, "label")} sx={{ p: 0.25 }} />
                </Box>
              ))}
            </>
          )}

          {categories.length > 0 && uniqueCustomIcons.length > 0 && <Divider sx={{ my: 1 }} />}

          {uniqueCustomIcons.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Custom icons</Typography>
              {uniqueCustomIcons.map((iconName) => (
                <Box key={iconName} sx={{ display: "flex", alignItems: "center", py: 0.25 }}>
                  <img src={`/map-icons/${iconName}.png`} alt="" style={{ width: 18, height: 18, objectFit: "contain", marginRight: 6, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {iconName.replace(/^custom_/, "").replace(/_/g, " ")}
                  </Typography>
                  <input
                    type="number"
                    min={0.25}
                    max={10}
                    step={0.25}
                    value={getScale(iconName)}
                    onChange={(e) => setScale(iconName, parseFloat(e.target.value))}
                    style={{ width: 48, fontSize: 12, padding: "2px 4px", border: "1px solid #ccc", borderRadius: 4, marginRight: 2 }}
                  />
                  <Checkbox size="small" checked={isIconVisible(iconName)} onChange={() => toggleVis(iconName, "icon")} sx={{ p: 0.25 }} />
                  <Checkbox size="small" checked={isLabelVisible(iconName)} onChange={() => toggleVis(iconName, "label")} sx={{ p: 0.25 }} />
                </Box>
              ))}
            </>
          )}

          {areas.length > 0 && <Divider sx={{ my: 1 }} />}

          {areas.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Areas</Typography>
              {areas.map((area) => (
                <Box key={area.id} sx={{ display: "flex", alignItems: "center", py: 0.25 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: area.color, mr: 0.75, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>{area.name || area.id}</Typography>
                  <Box sx={{ width: 54, flexShrink: 0 }} />
                  <Tooltip title="Show area">
                    <Checkbox size="small" checked={isFeatureVisible(`area_${area.id}`)} onChange={() => toggleFeature(`area_${area.id}`)} sx={{ p: 0.25 }} />
                  </Tooltip>
                  <Tooltip title="Show label">
                    <Checkbox size="small" checked={isFeatureVisible(`area_label_${area.id}`)} onChange={() => toggleFeature(`area_label_${area.id}`)} sx={{ p: 0.25 }} />
                  </Tooltip>
                </Box>
              ))}
            </>
          )}

          {lines.length > 0 && <Divider sx={{ my: 1 }} />}

          {lines.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Lines</Typography>
              {lines.map((line) => (
                <Box key={line.id} sx={{ display: "flex", alignItems: "center", py: 0.25 }}>
                  <Box sx={{ width: 12, height: 3, bgcolor: line.color, mr: 0.75, flexShrink: 0, borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>{line.name || line.id}</Typography>
                  <Checkbox size="small" checked={isFeatureVisible(`line_${line.id}`)} onChange={() => toggleFeature(`line_${line.id}`)} sx={{ p: 0.25 }} />
                </Box>
              ))}
            </>
          )}

          {allTags.length > 0 && <Divider sx={{ my: 1 }} />}

          {allTags.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Tags</Typography>
              {allTags.map((tag) => (
                <Box key={tag} sx={{ display: "flex", alignItems: "center", py: 0.25 }}>
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>{tag}</Typography>
                  <Checkbox size="small" checked={isTagVisible(tag)} onChange={() => toggleTag(tag)} sx={{ p: 0.25 }} />
                </Box>
              ))}
            </>
          )}

          <Divider sx={{ my: 1 }} />
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </Paper>
      )}

      <Map
        ref={mapRef}
        mapboxAccessToken={MAP_TOKEN}
        mapStyle="mapbox://styles/mapbox/light-v10"
        style={{ width: "100%", height: "100%" }}
        preserveDrawingBuffer
        initialViewState={{ longitude: 0, latitude: 51.5, zoom: 14 }}
        onLoad={(e) => {
          requestAnimationFrame(() => {
            e.target.resize();
            if (bounds) {
              e.target.fitBounds(expandBounds(bounds), {
                padding: 60,
                animate: false,
                maxZoom: 18,
              });
            }
          });
        }}
      >
        {/* Areas */}
        <Source id="areas" type="geojson" data={areasFC}>
          <Layer {...areaFillLayer} />
          <Layer {...areaOutlineLayer} />
        </Source>

        {/* Lines */}
        <Source id="lines" type="geojson" data={linesFC}>
          <Layer {...lineLayer} />
        </Source>

        {/* POI markers — circle + glyph icon only (labels moved to HTML markers for z-ordering) */}
        <Source id="pois" type="geojson" data={poisFC}>
          <Layer {...poiCircleLayer} filter={categoryIconFilter as any} paint={dynamicCirclePaint as any} />
          <Layer {...poiIconLayer} filter={categoryIconFilter as any} layout={{ ...poiIconLayer.layout, "text-size": dynamicTextSize as any }} />
        </Source>

        {/* Custom icon images — lowest z-order among HTML markers */}
        {visibleCustomIconPois.map((poi) => {
          const size = 36 * (poi.iconScale ?? 1) * getScale(poi.iconName);
          return (
            <Marker key={`icon-${poi.id}`} longitude={poi.lng} latitude={poi.lat} anchor="center">
              <img
                src={`/map-icons/${poi.iconName}.png`}
                alt=""
                style={{ width: size, height: size, objectFit: "contain", pointerEvents: "none" }}
              />
            </Marker>
          );
        })}

        {/* Area labels — above custom icon images */}
        {visibleAreaLabels.map((area) => (
          <Marker key={`area-label-${area.id}`} longitude={area.lng} latitude={area.lat} anchor="center">
            <div
              style={{
                color: "#ffffff",
                fontSize: 14,
                fontFamily: "sans-serif",
                fontWeight: 700,
                whiteSpace: "nowrap",
                textAlign: "center",
                pointerEvents: "none",
                textShadow: "0 0 4px rgba(0,0,0,0.7), 0 0 4px rgba(0,0,0,0.7), 0 0 4px rgba(0,0,0,0.7)",
              }}
            >
              {area.name}
            </div>
          </Marker>
        ))}

        {/* Category POI labels as HTML markers — above icon markers */}
        {visibleCategoryPoisWithLabels.map((poi) => (
          <Marker key={`cat-label-${poi.id}`} longitude={poi.lng} latitude={poi.lat} anchor="top" offset={[0, 14]}>
            <div
              style={{
                color: "#fff9f0",
                fontSize: 11 * getScale(poi.category),
                fontFamily: "sans-serif",
                fontWeight: 600,
                whiteSpace: "nowrap",
                textAlign: "center",
                pointerEvents: "none",
                textShadow: "0 0 3px rgba(0,0,0,0.6), 0 0 3px rgba(0,0,0,0.6)",
              }}
            >
              {poi.name}
            </div>
          </Marker>
        ))}

        {/* Custom icon labels — highest z-order, rendered last */}
        {visibleCustomIconPois
          .filter((poi) => isLabelVisible(poi.iconName))
          .map((poi) => {
            const size = 36 * (poi.iconScale ?? 1) * getScale(poi.iconName);
            return (
              <Marker
                key={`label-${poi.id}`}
                longitude={poi.lng}
                latitude={poi.lat}
                anchor="top"
                offset={[0, size / 2 + 2]}
              >
                <div
                  style={{
                    color: "#fff9f0",
                    fontSize: 11,
                    fontFamily: "sans-serif",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    pointerEvents: "none",
                    textShadow: "0 0 3px rgba(0,0,0,0.6), 0 0 3px rgba(0,0,0,0.6)",
                  }}
                >
                  {poi.name}
                </div>
              </Marker>
            );
          })}

      </Map>
    </Box>
  );
};
